class LiveSearch {
  constructor(root) {
    this.root = root;
    this.trigger = root.querySelector('[data-live-search-trigger]');
    this.overlay = root.querySelector('[data-live-search-overlay]');
    this.input = root.querySelector('[data-live-search-input]');
    this.closeBtn = root.querySelector('[data-live-search-close]');
    this.body = root.querySelector('[data-live-search-body]');

    this.debounceTimer = null;
    this.currentQuery = '';
    this.minChars = 2;
    this.debounceMs = 250;
    this.storageKey = 'enduristan_recently_viewed';
    this.articleStorageKey = 'enduristan_recently_viewed_articles';

    this.bindEvents();
  }

  bindEvents() {
    this.trigger.addEventListener('click', (e) => {
      e.preventDefault();
      this.open();
    });
    this.closeBtn.addEventListener('click', () => this.close());
    this.overlay.addEventListener('click', (e) => {
      if (e.target === this.overlay) this.close();
    });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && !this.overlay.hidden) this.close();
    });
    this.input.addEventListener('input', (e) => this.onInput(e.target.value));
  }

  open() {
    this.overlay.hidden = false;
    document.body.style.overflow = 'hidden';
    this.input.value = '';
    this.showRecentlyViewed();
    setTimeout(() => this.input.focus(), 50);
  }

  close() {
    this.overlay.hidden = true;
    document.body.style.overflow = '';
  }

  onInput(value) {
    clearTimeout(this.debounceTimer);
    if (value.trim().length < this.minChars) {
      this.showRecentlyViewed();
      return;
    }
    this.showLoading();
    this.debounceTimer = setTimeout(() => this.fetchResults(value.trim()), this.debounceMs);
  }

  getRecentlyViewed() {
    try {
      return JSON.parse(localStorage.getItem(this.storageKey) || '[]');
    } catch (e) {
      return [];
    }
  }

  getRecentlyViewedArticles() {
    try {
      return JSON.parse(localStorage.getItem(this.articleStorageKey) || '[]');
    } catch (e) {
      return [];
    }
  }

  showRecentlyViewed() {
    const items = this.getRecentlyViewed();
    const articles = this.getRecentlyViewedArticles();

    if (items.length === 0 && articles.length === 0) {
      this.body.innerHTML = `<div class="live-search__empty">Nog geen recent bekeken producten.</div>`;
      return;
    }

    let html = '';

    if (items.length > 0) {
      const cards = items.map((p) => `
        <a href="${p.url}" class="live-search__card">
          <img class="live-search__card-image" src="${p.image}" alt="${p.title}" loading="lazy">
          <span class="live-search__card-title">${p.title}</span>
          <span class="live-search__card-price">${this.formatPriceFromCents(p.price)}</span>
        </a>
      `).join('');
      html += `
        <div class="live-search__section-header">
          <span class="live-search__section-label">Recent bekeken</span>
          <button type="button" class="live-search__clear-recent" data-clear-recent-products>Wissen</button>
        </div>
        <div class="live-search__grid">${cards}</div>
      `;
    }

    if (articles.length > 0) {
      const blogItems = articles.map((a) => this.renderBlogItem(a)).join('');
      html += `
        <div class="live-search__section-header">
          <span class="live-search__section-label">Recent bekeken blogs</span>
          <button type="button" class="live-search__clear-recent" data-clear-recent-articles>Wissen</button>
        </div>
        <div class="live-search__blog-list">${blogItems}</div>
      `;
    }

    this.body.innerHTML = html;

    const clearProductsBtn = this.body.querySelector('[data-clear-recent-products]');
    if (clearProductsBtn) {
      clearProductsBtn.addEventListener('click', () => {
        localStorage.removeItem(this.storageKey);
        this.showRecentlyViewed();
      });
    }

    const clearArticlesBtn = this.body.querySelector('[data-clear-recent-articles]');
    if (clearArticlesBtn) {
      clearArticlesBtn.addEventListener('click', () => {
        localStorage.removeItem(this.articleStorageKey);
        this.showRecentlyViewed();
      });
    }
  }

  async fetchResults(query) {
    this.currentQuery = query;
    const params = new URLSearchParams({
      q: query,
      'resources[type]': 'product,article',
      'resources[limit]': 12,
      'resources[limit_scope]': 'each',
      'resources[options][unavailable_products]': 'last',
      'resources[options][fields]': 'title,product_type,variants.title,vendor,tag,author'
    });
    try {
      const response = await fetch(`/search/suggest.json?${params.toString()}`);
      const data = await response.json();
      if (query !== this.currentQuery) return;
      const products = data.resources.results.products || [];
      const articles = data.resources.results.articles || [];
      this.renderResults({ products, articles }, query);
    } catch (error) {
      console.error('Live search fout:', error);
      this.body.innerHTML = `<div class="live-search__empty">Er ging iets mis bij het zoeken.</div>`;
    }
  }

  renderResults({ products, articles }, query) {
    const total = products.length + articles.length;
    if (total === 0) {
      this.body.innerHTML = `<div class="live-search__empty">Geen resultaten gevonden voor "${query}"</div>`;
      return;
    }
    let html = '';
    if (products.length > 0) {
      html += `
        <div class="live-search__section-header"><span class="live-search__section-label">Producten</span></div>
        <div class="live-search__grid">${products.map((p) => this.renderProductCard(p)).join('')}</div>
      `;
    }
    if (articles.length > 0) {
      html += `
        <div class="live-search__section-header"><span class="live-search__section-label">Blogs</span></div>
        <div class="live-search__blog-list">${articles.map((a) => this.renderBlogItem(a)).join('')}</div>
      `;
    }
    const viewAllUrl = `/search?q=${encodeURIComponent(query)}&type=product`;
    html += `<a href="${viewAllUrl}" class="live-search__view-all">Bekijk alle resultaten voor "${query}"</a>`;
    this.body.innerHTML = html;
  }

  renderProductCard(product) {
    const image = product.image
      ? `<img class="live-search__card-image" src="${product.image}" alt="${product.title}" loading="lazy">`
      : `<div class="live-search__card-image"></div>`;
    const price = this.formatPriceDecimal(product.price);
    const comparePrice = product.compare_at_price_max > product.price
      ? `<span class="live-search__card-price--compare">${this.formatPriceDecimal(product.compare_at_price_max)}</span>`
      : '';
    const priceClass = comparePrice ? 'live-search__card-price--sale' : '';
    return `
      <a href="${product.url}" class="live-search__card">
        ${image}
        <span class="live-search__card-title">${product.title}</span>
        <span class="live-search__card-price ${priceClass}">${comparePrice}${price}</span>
      </a>
    `;
  }

  renderBlogItem(article) {
    const image = article.image
      ? `<img class="live-search__blog-image" src="${article.image}" alt="${article.title}" loading="lazy">`
      : `<div class="live-search__blog-image"></div>`;
    return `
      <a href="${article.url}" class="live-search__blog-item">
        ${image}
        <span class="live-search__blog-title">${article.title}</span>
      </a>
    `;
  }

  formatPriceFromCents(cents) {
    return new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR' }).format(cents / 100);
  }

  formatPriceDecimal(amount) {
    return new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR' }).format(amount);
  }

  showLoading() {
    this.body.innerHTML = `<div class="live-search__loading">Zoeken...</div>`;
  }
}

document.querySelectorAll('[data-live-search]').forEach((root) => new LiveSearch(root));
